<template>
	<div :class="{'has-columns': $slots.sidebar, 'is-optional': isOptional}" class="onboarding-step bg-gradient" :data-step="currentStep && currentStep.routeName">

		<slot name="notice-top"></slot>

		<slot name="top" />

		<slot name="heading" />

		<PageContent :heading="heading">
			<template #header>
				<slot name="header" /></template>
			<template #default>
				<div v-if="$slots.sidebar" class="onboarding-step-columns">
					<div class="onboarding-step-column onboarding-step-left-column">
						<slot v-bind="scope" />
					</div>
					<aside class="onboarding-step-column onboarding-step-right-column">
						<slot name="sidebar" v-bind="scope" />
					</aside>
				</div>
				<slot v-else v-bind="scope" />

				<slot name="before-actions" />

				<!-- Actions -->
				<div v-if="backButtonIsVisible || nextButtonIsVisible || skipButtonIsVisible" class="mt-24 flex-right">
					<button v-if="backButtonIsVisible" class="btn btn-large align-left" :disabled="backButtonIsDisabled" @click="previous">
						{{ backButtonText }}
					</button>

					<button v-if="skipButtonIsVisible" class="btn btn-large" :disabled="skipButtonIsDisabled" @click="skip">
						Skip for now
					</button>

					<button v-if="nextButtonIsVisible" class="btn btn-large btn-green" :disabled="nextButtonIsDisabled" :type="formId ? 'submit' : 'button'" :form="formId" @click="onNextButtonClick">
						{{ nextButtonText }}
					</button>
				</div>

				<div class="onboarding-step-errors" v-if="errorMessage.length">
					<p v-for="(message, index) in errorMessage" :key="`onboarding-error-${index}`" class="onboarding-step-error">
						{{ message }}
					</p>
				</div>
			</template>
		</PageContent>

		<slot name="bottom"></slot>
	</div>
</template>

<script>
import { toNonEmptyStringArray, toString } from '@/helpers/convert';

export default {
  name: 'OnboardingStep',
  data() {
    return {
      /** @type {boolean} */
      isNavigatingData: false,
      /** @type {string | null} */
      navigationError: null,
    };
  },
  props: {
    /**
     * Determines the visibility and functionality of the Back button.
     * - If this is `true`, then the Back button is only visible if there is an accessible previous
     *   step in the current onboarding path.
     * - Otherwise, if this is any `false`-like value, then the Back button is hidden.
     * - Otherwise, the Back button is visible, and this value is used as its destination route.
     *
     * @type {import('vue/types/options').PropOptions<unknown>}
     */
    backButton: {
      default: true,
    },
    /**
     * An optional function that is invoked whenever an attempt is made to complete the
     * current step and advance to the next step.
     * - Its `this` value is a reference to the parent component.
     * - If this function returns `false` (or a promise that resolves with `false`), then the
     *   operation is canceled and the user will remain on the current step.
     *
     * If this function is not provided, then attempts to complete the current step are always
     * successful.
     *
     * @type {import('vue/types/options').PropOptions<((this: Vue) => any) | null | undefined>}
     */
    commit: {
      type: Function,
      default: null,
    },
    /**
     * If this is `true`, then the button that advances to the next onboarding step is disabled.
     *
     * Note that only *disables* the button. To prevent the button from rendering at all, use
     * `noNextButton` instead.
     *
     * @type {import('vue/types/options').PropOptions<boolean | null | undefined>}
     */
    disableNext: {
      type: Boolean,
      default: false,
    },
    /**
     * The error(s) currently preventing the user from continuing to the next step.
     *
     * @type {import('vue/types/options').PropOptions<unknown>}
     */
    error: {
      default: null,
    },
    /**
     * The ID of the form that is submitted when an attempt is made to advance to the next step.
     *
     * @type {import('vue/types/options').PropOptions<string | null | undefined>}
     */
    formId: {
      type: String,
      default: null,
    },
    /**
     * The page heading.
     *
     * @type {import('vue/types/options').PropOptions<unknown>}
     */
    heading: {
      default: null,
    },
    /**
     * If this is `true`, then all navigation buttons are disabled.
     *
     * @type {import('vue/types/options').PropOptions<boolean | null | undefined>}
     */
    navigating: {
      type: Boolean,
      default: false,
    },
    /**
     * Determines the visibility (and, optionally, the text label) of the Next button.
     * - If this is `true`, then the Next button is visible and uses the default text label.
     * - Otherwise, if this is any `false`-like value, then the Next button is hidden.
     * - Otherwise, the Next button is visible, and this value is used as its text label.
     *
     * @type {import('vue/types/options').PropOptions<unknown>}
     */
    nextButton: {
      default: true,
    },
  },
  computed: {
    /**
     * @returns {boolean}
     */
    backButtonIsDisabled() {
      return this.isNavigating;
    },
    /**
     * @returns {boolean}
     */
    backButtonIsVisible() {
		if (this.$route.name == 'onboardingAvailability') return false;
      return !!this.previousRoute;
    },
    /**
     * @returns {string}
     */
    backButtonText() {
      return 'Back';
    },
    /**
     * @returns {import('@/helpers/onboarding').OnboardingStep | null}
     */
    currentStep() {
      return this.$store.getters['onboarding/currentStep'];
    },
    /**
     * @returns {number | null}
     */
    currentStepIndex() {
      return this.$store.getters['onboarding/currentStepIndex'];
    },
    /**
     * @returns {boolean}
     */
    debug() {
      return this.$store.getters['onboarding/debug'];
    },
    /**
     * @returns {readonly string[]}
     */
    errorMessage() {
      return toNonEmptyStringArray([this.error, this.navigationError], (e) => e.trim());
    },
    event: {
      /**
       * @returns {JobEvent | null}
       */
      get() {
        return this.$store.getters['onboarding/event'];
      },
      /**
       * @param {JobEvent | null} event
       * @returns {void}
       */
      set(value) {
        this.$store.commit('onboarding/setEvent', value);
      },
    },
    /**
     * @returns {boolean}
     */
    isAlreadyOnboarded() {
      return !!this.$store.getters['onboarding/isAlreadyOnboarded'];
    },
    /**
     * @returns {boolean}
     */
    isLastStep() {
      return this.currentStepIndex === this.steps.length - 1;
    },
    isNavigating: {
      /**
       * @returns {boolean}
       */
      get() {
        return !!(this.navigating || this.isNavigatingData);
      },
      /**
       * @param {boolean} value
       * @returns {void}
       */
      set(value) {
        this.isNavigatingData = !!value;
      },
    },
    /**
     * @returns {boolean}
     */
    isOptional() {
      return !!this.currentStep?.isOptional;
    },
    /**
     * @returns {boolean}
     */
    isUpgrading() {
      return !!this.$store.getters['onboarding/isUpgrading'];
    },
    /**
     * @returns {boolean}
     */
    nextButtonIsDisabled() {
      return !!(this.disableNext || this.isNavigating);
    },
    /**
     * @returns {boolean}
     */
    nextButtonIsVisible() {
		if (this.$route.name == 'onboardingAvailability') return false;
      return !!this.nextButton;
    },
    /**
     * @returns {string}
     */
    nextButtonText() {
      const { nextButton } = this;
      return (
        (nextButton && nextButton !== true && toString(nextButton).trim()) ||
        (this.isLastStep ? 'All Done!' : 'Continue')
      );
    },
    /**
     * @returns {import('vue-router').Location}
     */
    nextRoute() {
      return this.$store.getters['onboarding/nextRoute'];
    },
    /**
     * @returns {import('@/helpers/onboarding').OnboardingStep | null}
     */
    nextStep() {
      return this.$store.getters['onboarding/nextStep'];
    },
    /**
     * @returns {SubscriptionLevel | null}
     */
    originalPlan() {
      return this.isAlreadyOnboarded ? this.$store.getters['subscriptions/currentLevel'] : null;
    },
    /**
     * @returns {SubscriptionLevel | null}
     */
    plan() {
      return this.$store.getters['onboarding/plan'];
    },
    /**
     * @returns {import('vue-router').Location | null}
     */
    previousRoute() {
      const { backButton } = this;
      return backButton === true
        ? this.$store.getters['onboarding/previousRoute']
        : backButton
        ? this.$router.resolve(backButton).normalizedTo
        : null;
    },
    /**
     * @returns {{ formId: string; next(): Promise<boolean>; previous(): Promise<boolean>; skip(): Promise<boolean>; }}
     */
    scope() {
      return {
        formId: this.formId,
        next: this.next.bind(this),
        previous: this.previous.bind(this),
        skip: this.skip.bind(this),
      };
    },
    /**
     * @returns {boolean}
     */
    skipButtonIsDisabled() {
      return this.isNavigating;
    },
    /**
     * @returns {boolean}
     */
    skipButtonIsVisible() {
      return !!this.isOptional;
    },
    /**
     * @returns {readonly import('@/helpers/onboarding').OnboardingStep[]}
     */
    steps() {
      return this.$store.getters['onboarding/steps'];
    },
    /**
     * @returns {boolean}
     */
    wasStudio() {
      /** @type {{ originalPlan: SubscriptionLevel | null }} */
      const { originalPlan } = this;
      return !!originalPlan && !originalPlan.canBeHired;
    },
  },
  methods: {
    /**
     * @param {*} error
     * @returns {string}
     */
    getDefaultErrorMessage(error) {
      error = toString(error).trim();
      return `We couldn’t process your request because of ${
        error ? `an error (${error})` : 'an unknown error'
      }. Please try again later.`;
    },
    /**
     * @param {*} error
     * @returns {string}
     */
    getErrorMessage(error) {
      if (!this.isAxiosError(error)) {
        return this.getDefaultErrorMessage(error instanceof Error ? error.message : error);
      }
      const { response } = error;
      const apiMessages = toNonEmptyStringArray(response?.data?.message, (m) => m.trim());
      return apiMessages.length
        ? apiMessages.join(' ')
        : this.getDefaultErrorMessage(error.message || response?.status);
    },
    /**
     * @returns {Promise<void>}
     */
    async goToNextRoute() {
      /** @type {{ $store: AppStore; isLastStep: boolean }} */
      const { $store: store, isLastStep } = this;
      /** @type {{ nextRoute: import('vue-router').Location }} */
      let { nextRoute } = this;
      try {
        /** @type {SubscriptionLevel | null} */
        let plan;
        if (
          this.isUpgrading &&
          this.wasStudio &&
          this.nextStep?.routeName === 'onboardingAvailability' &&
          (plan = this.plan) &&
          plan.canBeHired
        ) {
          /*
           * If upgrading from studio to photographer, and the next step is availability, then
           * go ahead and save the subscription level change, because the user will be prohibited
           * from entering availability unless their subscription level allows it.
           */
          await this.lockPath();
          await store.dispatch('onboarding/savePlan', plan.roleId);
          plan = this.plan;
        } else if (isLastStep) {
          // If this is the last step of the current onboarding path, then:

          // Lock the onboarding path.
          await this.lockPath();

          // If the user filled out an event form in a previous step, then create that event now.
          /** @type {{ event: JobEvent | null }} */
          let { event } = this;
          if (event) {
            let eventId = event.id;
            if (!eventId) {
              event = await store.dispatch('events/createEvent', event);
              eventId = event.id;
              this.event = event;
            }
            store.commit('events/setSearchEvent', event);
            store.commit('events/setSearchQueue', null);
            nextRoute = { name: this.$routes.FindHelpSearch.name, params: { eventId } };
            store.commit(
              'events/setSearchHiringUserProfile',
              store.getters['onboarding/hiringUserProfile']
            );
            store.commit('onboarding/setHiringUserProfile', null);
          }
        }

        // Redirect to the next route.
        await this.$router.push(nextRoute);

        if (isLastStep) {
          // If this was the last step of the current onboarding path, then:

          // Complete the onboarding process and show the welcome modal if needed.
          if (!store.getters['onboarding/isAlreadyOnboarded']) {
            store.dispatch('onboarding/complete');
            store.commit('onboarding/setShowWelcomeModal', true);
            this.$gtmDispatchOnboardingOptionEvent(
              6,
              (plan || this.plan)?.canBeHired ? 'Get Hired' : 'Find Talent'
            );
            this.$gtmDispatchOnboardingStepEvent(6, 'Onboarding Process Complete');
          }

          // Initialize the Intercom chat widget if it's not initialized already.
          if (store.getters['onboarding/isNewAccount']) {
            store.dispatch('auth/intercomLogin');
          }

          // Clear the onboarding state.
          store.commit('onboarding/clear');
        }
      } finally {
        if (isLastStep) {
          await this.unlockPath();
        }
      }
    },
    /**
     * @param {*} value
     * @returns {value is import('axios').AxiosError}
     */
    isAxiosError(value) {
      return !!value.isAxiosError;
    },
    /**
     * @returns {Promise<void>}
     */
    lockPath() {
      return this.$store.dispatch('onboarding/lockPath');
    },
    /**
     * @param {() => unknown} callback
     * @returns {Promise<boolean>}
     */
    async navigate(callback) {
      if (this.isNavigating) {
        return false;
      }
      let result = false;
      this.isNavigating = true;
      this.$emit('navigate:before');
      try {
        result = (await callback()) !== false;
        this.$emit(`navigate:${result ? 'success' : 'cancel'}`);
      } catch (error) {
        this.navigationError = this.getErrorMessage(error);
        console.error(error);
        console.dir(error);
        this.$emit('navigate:error', error);
      }
      this.isNavigating = false;
      this.$emit('navigate:after', result);
      return result;
    },
    /**
     * Completes the current step and navigates to the next step in the current onboarding
     * path.
     *
     * @returns {Promise<boolean>}
     * A promise that resolves with `true` if the step was completed successfully, or `false`
     * if the operation was canceled (e.g. due to an invalid form field) and the step remains
     * incomplete. Note that this promise does not resolve until **after** any resulting
     * router navigation is complete.
     */
    next() {
      return this.navigate(async () => {
        /** @type {((this: Vue) => any) | null | undefined} */
        let commit;
        /** @type {Vue | undefined} */
        let parent;
        if (
          !this.debug &&
          typeof (commit = this.commit) === 'function' &&
          (parent = this.$parent) &&
          (await commit.call(parent)) === false
        ) {
          return false;
        }
        await this.goToNextRoute();
        return true;
      });
    },
    /**
     * @returns {void}
     */
    onNextButtonClick() {
      if (!this.formId) {
        this.next();
      }
    },
    /**
     * Navigates to the previous step in the current onboarding path.
     *
     * @returns {Promise<boolean>}
     * A promise that resolves with `true` if navigation was successful, or `false` if
     * navigation was canceled.
     */
    previous() {
      return this.navigate(async () => {
        const { previousRoute } = this;
        if (previousRoute) {
          await this.$router.push(previousRoute);
        } else {
          this.$router.back();
        }
        return true;
      });
    },
    /**
     * Skips the current step, emits a `skip` event, and navigates to the next step in the
     * current onboarding path.
     *
     * @returns {Promise<boolean>}
     * A promise that resolves with `true` if the step was skipped successfully, or `false` if
     * the step could not be skipped.
     */
    skip() {
      return this.navigate(async () => {
        if (!this.isOptional) {
          return false;
        }
        this.$emit('skip');
        await this.goToNextRoute();
        return true;
      });
    },
    /**
     * @returns {Promise<void>}
     */
    unlockPath() {
      return this.$store.dispatch('onboarding/unlockPath');
    },
  },
  watch: {
    /**
     * @param {unknown} value
     * @returns {void}
     */
    isNavigatingData(value) {
      if ((value = !!value) !== !!this.navigating) {
        this.$emit('update:loading', value);
      }
    },
  },
};
</script>
